import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { PlusCircle, Trash2, Download, FileText } from 'lucide-react';
import { generateInvoicePDF } from '@/utils/pdfGenerator';

const InvoiceGenerator = () => {
  const [invoicePrefix, setInvoicePrefix] = useState('001');
  const [invoiceSuffix, setInvoiceSuffix] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [items, setItems] = useState([
    { id: 1, description: '', quantity: 0, unit: 'Pcs', price: 0 }
  ]);

  useEffect(() => {
    const lastNum = parseInt(localStorage.getItem('lastInvoiceNum') || '0');
    setInvoicePrefix(String(lastNum + 1).padStart(3, '0'));
  }, []);

  useEffect(() => {
    const date = new Date(invoiceDate);
    const monthRoman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][date.getMonth()];
    const year = date.getFullYear();
    setInvoiceSuffix(`/INV/O.R/KOP/SA/${monthRoman}/${year}`);
  }, [invoiceDate]);

  const getFullInvoiceNumber = () => `${invoicePrefix}${invoiceSuffix}`;

  const addItem = () => {
    const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
    setItems([...items, { id: newId, description: '', quantity: 0, unit: 'Pcs', price: 0 }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const calculateItemTotal = (item) => {
    return item.quantity * item.price;
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID').format(amount);
  };

  const handleGeneratePDF = () => {
    const fullInvoiceNumber = getFullInvoiceNumber();
    const invoiceData = {
      invoiceNumber: fullInvoiceNumber,
      invoiceDate,
      customerName,
      customerAddress,
      customerPhone,
      items: items.map((item, index) => ({
        no: index + 1,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        price: item.price,
        total: calculateItemTotal(item)
      })),
      grandTotal: calculateGrandTotal()
    };

    // Save nomor urut ke localStorage
    localStorage.setItem('lastInvoiceNum', parseInt(invoicePrefix));

    generateInvoicePDF(invoiceData);
  };

  const isFormValid = () => {
    return customerName && customerAddress && items.some(item => item.description && item.quantity > 0);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-2">
          <FileText className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold text-gray-800">Invoice Generator</h1>
        </div>
        <p className="text-gray-600">KOPERASI KONSUMEN TOENGGAK SEMI MAKMUR SEGORO AGUNG</p>
      </div>

      <Card className="shadow-xl">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <CardTitle className="text-2xl">Buat Invoice Baru</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Invoice Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="invoiceNumber" className="text-sm font-semibold">Nomor Invoice</Label>
              <div className="flex items-center gap-0">
                <Input
                  id="invoicePrefix"
                  data-testid="invoice-prefix-input"
                  value={invoicePrefix}
                  onChange={(e) => setInvoicePrefix(e.target.value)}
                  className="font-mono text-sm w-20 rounded-r-none border-r-0 text-center font-bold"
                  placeholder="027"
                />
                <Input
                  id="invoiceSuffix"
                  data-testid="invoice-suffix-display"
                  value={invoiceSuffix}
                  disabled
                  className="bg-gray-50 font-mono text-sm flex-1 rounded-l-none text-gray-600"
                />
              </div>
              <p className="text-xs text-gray-500">Ubah nomor urut di sebelah kiri, sisanya otomatis</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceDate" className="text-sm font-semibold">Tanggal Invoice</Label>
              <Input
                id="invoiceDate"
                data-testid="invoice-date-input"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>

          <Separator />

          {/* Customer Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Informasi Pelanggan</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="customerName" className="text-sm font-semibold">Nama Pelanggan *</Label>
                <Input
                  id="customerName"
                  data-testid="customer-name-input"
                  placeholder="Masukkan nama pelanggan"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerAddress" className="text-sm font-semibold">Alamat Pelanggan *</Label>
                <Input
                  id="customerAddress"
                  data-testid="customer-address-input"
                  placeholder="Masukkan alamat pelanggan"
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerPhone" className="text-sm font-semibold">Telepon</Label>
                <Input
                  id="customerPhone"
                  data-testid="customer-phone-input"
                  placeholder="Masukkan nomor telepon"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700">Item Produk</h3>
              <Button
                onClick={addItem}
                variant="outline"
                size="sm"
                data-testid="add-item-button"
                className="gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                Tambah Item
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <Card key={item.id} className="border-2 border-gray-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-12 md:col-span-4">
                        <Label htmlFor={`desc-${item.id}`} className="text-xs font-semibold">Deskripsi *</Label>
                        <Input
                          id={`desc-${item.id}`}
                          data-testid={`item-description-${index}`}
                          placeholder="Nama produk"
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label htmlFor={`qty-${item.id}`} className="text-xs font-semibold">Jumlah *</Label>
                        <Input
                          id={`qty-${item.id}`}
                          data-testid={`item-quantity-${index}`}
                          type="number"
                          placeholder="0"
                          value={item.quantity || ''}
                          onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label htmlFor={`unit-${item.id}`} className="text-xs font-semibold">Satuan</Label>
                        <Input
                          id={`unit-${item.id}`}
                          data-testid={`item-unit-${index}`}
                          placeholder="Pcs"
                          value={item.unit}
                          onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label htmlFor={`price-${item.id}`} className="text-xs font-semibold">Harga (Rp) *</Label>
                        <Input
                          id={`price-${item.id}`}
                          data-testid={`item-price-${index}`}
                          type="number"
                          placeholder="0"
                          value={item.price || ''}
                          onChange={(e) => updateItem(item.id, 'price', parseInt(e.target.value) || 0)}
                          className="mt-1"
                        />
                      </div>
                      <div className="col-span-10 md:col-span-1">
                        <div className="text-xs font-semibold text-gray-600 mb-1">Total</div>
                        <div className="text-sm font-bold text-blue-600" data-testid={`item-total-${index}`}>
                          Rp. {formatCurrency(calculateItemTotal(item))}
                        </div>
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <Button
                          onClick={() => removeItem(item.id)}
                          variant="destructive"
                          size="icon"
                          data-testid={`remove-item-${index}`}
                          disabled={items.length === 1}
                          className="w-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Separator />

          {/* Grand Total */}
          <div className="flex justify-end">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 min-w-[300px]">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-700">TOTAL:</span>
                <span className="text-2xl font-bold text-blue-600" data-testid="grand-total">
                  Rp. {formatCurrency(calculateGrandTotal())}
                </span>
              </div>
            </div>
          </div>

          {/* Generate PDF Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleGeneratePDF}
              disabled={!isFormValid()}
              data-testid="generate-pdf-button"
              size="lg"
              className="gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-6 text-lg"
            >
              <Download className="w-5 h-5" />
              Generate & Download Invoice PDF
            </Button>
          </div>

          {!isFormValid() && (
            <p className="text-center text-sm text-red-500">* Harap isi nama pelanggan, alamat, dan minimal 1 item produk dengan lengkap</p>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>Invoice akan di-download dalam format PDF</p>
        <p className="mt-1">Jl. Syeh Jumadil Kubro, Sentonorejo, Trowulan, Mojokerto, Jawa Timur</p>
      </div>
    </div>
  );
};

export default InvoiceGenerator;